import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuthContext } from "@/contexts/AuthContext";

export const usePushNotifications = () => {
  const { user } = useAuthContext();
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [permission, setPermission] = useState<NotificationPermission>("default");
  const [loading, setLoading] = useState(false);

  // Check if push notifications are supported
  useEffect(() => {
    const supported = 
      "serviceWorker" in navigator && 
      "PushManager" in window && 
      "Notification" in window;
    setIsSupported(supported);

    if (supported) {
      setPermission(Notification.permission);
    }
  }, []);

  // Check subscription status on mount
  useEffect(() => {
    const checkSubscription = async () => {
      if (!isSupported || !user) return;

      try {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        setIsSubscribed(!!subscription);
      } catch (error) {
        console.error("Error checking subscription:", error);
      }
    };

    checkSubscription();
  }, [isSupported, user]);

  // Register service worker
  const registerServiceWorker = useCallback(async () => {
    if (!("serviceWorker" in navigator)) {
      throw new Error("Service workers not supported");
    }

    const registration = await navigator.serviceWorker.register("/sw.js", {
      scope: "/"
    });

    // Wait for the service worker to be ready
    await navigator.serviceWorker.ready;
    return registration;
  }, []);

  // Subscribe to push notifications
  const subscribe = useCallback(async () => {
    if (!isSupported || !user) return { success: false, error: "Not supported" };

    setLoading(true);
    try {
      // Request permission
      const permissionResult = await Notification.requestPermission();
      setPermission(permissionResult);

      if (permissionResult !== "granted") {
        return { success: false, error: "Permission denied" };
      }

      // Register service worker
      const registration = await registerServiceWorker();

      // Get VAPID public key from edge function
      const { data: vapidData, error: vapidError } = await supabase.functions.invoke(
        "push-notifications",
        {
          body: { action: "get-vapid-key" }
        }
      );

      if (vapidError || !vapidData?.publicKey) {
        console.error("VAPID key error:", vapidError);
        return { success: false, error: "Failed to get push configuration" };
      }

      // Subscribe to push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidData.publicKey)
      });

      // Save subscription to database
      const subscriptionJSON = subscription.toJSON();
      const { error: saveError } = await supabase
        .from("notification_preferences")
        .upsert({
          user_id: user.id,
          push_token: JSON.stringify(subscriptionJSON),
          likes_enabled: true,
          matches_enabled: true,
          messages_enabled: true,
          pops_enabled: true
        }, { onConflict: "user_id" });

      if (saveError) {
        console.error("Error saving subscription:", saveError);
        return { success: false, error: "Failed to save subscription" };
      }

      setIsSubscribed(true);
      return { success: true };
    } catch (error) {
      console.error("Subscription error:", error);
      return { success: false, error: (error as Error).message };
    } finally {
      setLoading(false);
    }
  }, [isSupported, user, registerServiceWorker]);

  // Unsubscribe from push notifications
  const unsubscribe = useCallback(async () => {
    if (!isSupported || !user) return { success: false };

    setLoading(true);
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        await subscription.unsubscribe();
      }

      // Remove from database
      await supabase
        .from("notification_preferences")
        .update({ push_token: null })
        .eq("user_id", user.id);

      setIsSubscribed(false);
      return { success: true };
    } catch (error) {
      console.error("Unsubscribe error:", error);
      return { success: false };
    } finally {
      setLoading(false);
    }
  }, [isSupported, user]);

  return {
    isSupported,
    isSubscribed,
    permission,
    loading,
    subscribe,
    unsubscribe
  };
};

// Helper to convert VAPID key
function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, "+")
    .replace(/_/g, "/");

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length) as Uint8Array<ArrayBuffer>;

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}
